import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Cell, Column, flexRender, Row, Table } from '@tanstack/react-table';
import { Funnel } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { MobileCardRole } from '@/lib/table-query';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { ColumnLabel } from './column-label';
import {
  DataCardFilterDialog,
  filterableColumns,
  sortableColumns,
} from './data-card-filter-dialog';

type CardRole = MobileCardRole | 'select' | 'body';

/** Each visible column's place on the card: its `meta.mobile`, or the default (see `MobileCardRole`). */
export const getCardRoles = <TData,>(columns: Column<TData, unknown>[]): Map<string, CardRole> => {
  const hasTitle = columns.some((column) => column.columnDef.meta?.mobile === 'title');
  const defaultTitleId = hasTitle
    ? undefined
    : columns.find(
        (column) =>
          column.id !== 'select' && column.id !== 'actions' && !column.columnDef.meta?.mobile
      )?.id;

  return new Map(
    columns.map((column): [string, CardRole] => {
      const hint = column.columnDef.meta?.mobile;
      if (hint) return [column.id, hint];
      if (column.id === 'select') return [column.id, 'select'];
      if (column.id === 'actions') return [column.id, 'footer'];
      if (column.id === 'Status') return [column.id, 'badge'];
      if (column.id === defaultTitleId) return [column.id, 'title'];
      return [column.id, 'body'];
    })
  );
};

interface DataCardListProps<TData> {
  table: Table<TData>;
  isLoading: boolean;
  /** The message shown instead of cards (no rows, or a load error). */
  message?: { text: string; isError?: boolean };
  onRowClick?: (data: TData) => void;
  getRowClassName?: (data: TData) => string | undefined;
  /** The page's actions, on the left of the filter button's line. */
  actions?: React.ReactNode;
  /** Where to put the filter button instead of above the cards (e.g. a spot in the page header). */
  filterSlot?: HTMLElement | null;
  /** Above the cards: the table's `filterToolbar` (rendered in a table, it may return table rows). */
  filterToolbar?: React.ReactNode;
  /** A row's expanded content (rendered in a table, it may return table rows). */
  renderExpanded?: (row: Row<TData>) => React.ReactNode;
}

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

const pairOrder = <TData,>(column: Column<TData, unknown>) =>
  column.columnDef.meta?.mobileOrder ?? Number.MAX_SAFE_INTEGER;

/**
 * The rows of an `AdvanceDataTable` as cards, on a phone (the old portal's generic card list): a title
 * line with a status chip, label/value pairs two per line, and the row's actions in a footer. Sorting
 * and column filters move into a filter dialog, as the column headers are not shown.
 */
export const DataCardList = <TData,>({
  table,
  isLoading,
  message,
  onRowClick,
  getRowClassName,
  actions,
  filterSlot,
  filterToolbar,
  renderExpanded,
}: Readonly<DataCardListProps<TData>>) => {
  const { t } = useTranslation();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const roles = getCardRoles(table.getVisibleLeafColumns());
  const canFilter = filterableColumns(table).length > 0 || sortableColumns(table).length > 0;
  const { columnFilters, sorting } = table.getState();
  const activeCount = columnFilters.length + (sorting.length > 0 ? 1 : 0);

  const cellsOf = (row: Row<TData>, role: CardRole) =>
    row.getVisibleCells().filter((cell) => roles.get(cell.column.id) === role);
  const renderCell = (cell: Cell<TData, unknown>) =>
    flexRender(cell.column.columnDef.cell, cell.getContext());

  const renderCard = (row: Row<TData>) => {
    const titles = cellsOf(row, 'title');
    const subtitles = cellsOf(row, 'subtitle');
    const badges = cellsOf(row, 'badge');
    const pairs = row
      .getVisibleCells()
      .filter((cell) => ['body', 'full'].includes(roles.get(cell.column.id) ?? ''))
      // A stable sort: pairs without a `mobileOrder` keep their column order, after the others.
      .sort((a, b) => pairOrder(a.column) - pairOrder(b.column));
    // Footer text (e.g. a reason) on the left with its label, the row's actions on the right.
    const footer = cellsOf(row, 'footer');
    const footerText = footer.filter((cell) => cell.column.id !== 'actions');
    const footerActions = footer.filter((cell) => cell.column.id === 'actions');
    const hasSelect = roles.has('select');

    return (
      <div
        key={row.id}
        data-state={row.getIsSelected() ? 'selected' : undefined}
        onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        className={cn(
          'flex flex-col gap-3 rounded-lg border border-border bg-card p-4',
          onRowClick && 'cursor-pointer',
          row.getIsSelected() && 'border-primary bg-accent',
          getRowClassName?.(row.original)
        )}
      >
        <div className="flex items-start gap-3">
          {hasSelect && (
            <Checkbox
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              onClick={stopPropagation}
              aria-label={t('SELECT_ROW')}
              className="mt-0.5 border-2 border-muted-foreground data-[state=checked]:border-none"
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            {titles.map((cell) => (
              <div key={cell.id} className="break-words text-base font-semibold">
                {renderCell(cell)}
              </div>
            ))}
            {subtitles.map((cell) => (
              <div key={cell.id} className="break-words text-sm text-muted-foreground">
                {renderCell(cell)}
              </div>
            ))}
          </div>
          {badges.length > 0 && (
            <div className="flex shrink-0 flex-col items-end gap-1">
              {badges.map((cell) => (
                <div key={cell.id}>{renderCell(cell)}</div>
              ))}
            </div>
          )}
        </div>

        {pairs.length > 0 && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            {pairs.map((cell) => (
              <div
                key={cell.id}
                className={cn(
                  'flex min-w-0 flex-col gap-0.5',
                  roles.get(cell.column.id) === 'full' && 'col-span-2'
                )}
              >
                <dt className="text-xs text-muted-foreground">
                  <ColumnLabel table={table} column={cell.column} />
                </dt>
                <dd className="break-words text-sm font-medium">{renderCell(cell)}</dd>
              </div>
            ))}
          </dl>
        )}

        {footer.length > 0 && (
          // The text takes the full width it needs; buttons beside it drop to their own line first.
          <div className="flex flex-wrap items-end justify-between gap-3 border-t border-dashed border-border pt-3">
            {/* Two or more values pair up like the body; a single one (a reason) spans the width. */}
            <div className="grid min-w-[60%] flex-1 grid-cols-2 gap-x-4 gap-y-2 [&>:only-child]:col-span-2">
              {footerText.map((cell) => (
                <div key={cell.id} className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-xs text-muted-foreground">
                    <ColumnLabel table={table} column={cell.column} />
                  </span>
                  <span className="break-words text-sm">{renderCell(cell)}</span>
                </div>
              ))}
            </div>
            {footerActions.length > 0 && (
              <div
                className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2"
                onClick={stopPropagation}
              >
                {footerActions.map((cell) => (
                  <React.Fragment key={cell.id}>{renderCell(cell)}</React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}

        {renderExpanded && row.getIsExpanded() && (
          <table className="w-full">
            <tbody>{renderExpanded(row)}</tbody>
          </table>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="flex flex-col gap-3 rounded-lg border border-border p-4">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ));
    }
    if (message) {
      return (
        <div
          className={cn(
            'rounded-lg border border-border px-4 py-8 text-center text-sm break-words',
            message.isError && 'text-destructive'
          )}
        >
          {message.text}
        </div>
      );
    }
    return table.getRowModel().rows.map(renderCard);
  };

  const filterButton = canFilter && (
    <Button
      type="button"
      variant="outline"
      size="icon"
      // As tall as the date pickers, selects and buttons that sit beside it.
      className="relative size-9 shrink-0"
      aria-label={t('FILTER')}
      onClick={() => setFilterOpen(true)}
    >
      <Funnel className="size-5" weight={activeCount > 0 ? 'fill' : 'regular'} />
      {activeCount > 0 && (
        <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs text-primary-foreground">
          {activeCount}
        </span>
      )}
    </Button>
  );
  // The page may place the filter button elsewhere (e.g. its header); otherwise it heads the cards.
  const isFilterElsewhere = Boolean(filterSlot);
  // The table's select-all is the `select` column's header (it knows which rows can be selected).
  const selectHeader = table.getLeafHeaders().find((header) => header.column.id === 'select');
  const selectAll = selectHeader?.column.columnDef.header && !isLoading && !message && (
    <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border">
      {flexRender(selectHeader.column.columnDef.header, selectHeader.getContext())}
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-3">
      {filterSlot && filterButton && createPortal(filterButton, filterSlot)}
      {(actions || selectAll || (canFilter && !isFilterElsewhere)) && (
        <div className="flex items-center justify-between gap-2">
          {/* The actions take the room the filter button leaves (a picker can fill it). */}
          <div className="flex min-w-0 flex-1 items-center">{actions}</div>
          {selectAll}
          {!isFilterElsewhere && filterButton}
        </div>
      )}
      {filterToolbar && (
        <table className="w-full overflow-hidden rounded-lg">
          <thead>{filterToolbar}</thead>
        </table>
      )}
      {renderContent()}
      {canFilter && (
        <DataCardFilterDialog table={table} open={isFilterOpen} onOpenChange={setFilterOpen} />
      )}
    </div>
  );
};
